import { useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router";
import { CreditCard, Building2, ShieldCheck, UserPlus, Upload, BadgeAlert, CheckCircle2 } from "lucide-react";

type FormValues = {
	email: string;
	password: string;
	confirmPassword: string;
	photo: File | null;
	cardName: string;
	cardNumber: string;
	cardExpiry: string;
	cardCvv: string;
	companyName: string;
	tradeName: string;
	cnpj: string;
	alias: string;
	companyPhone: string;
};

type ValidationErrors = Partial<Record<keyof FormValues, string>>;

const inputClass =
	"mt-1 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

function onlyDigits(value: string) {
	return value.replace(/\D/g, "");
}

function formatCardNumber(value: string) {
	return onlyDigits(value)
		.slice(0, 16)
		.replace(/(.{4})/g, "$1 ")
		.trim();
}

function formatCardExpiry(value: string) {
	const digits = onlyDigits(value).slice(0, 4);
	if (digits.length < 3) {
		return digits;
	}
	return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function formatCnpj(value: string) {
	const digits = onlyDigits(value).slice(0, 14);
	return digits
		.replace(/^(\d{2})(\d)/, "$1.$2")
		.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
		.replace(/\.(\d{3})(\d)/, ".$1/$2")
		.replace(/(\d{4})(\d)/, "$1-$2");
}

function isValidAlias(alias: string) {
	return /^[A-Za-z0-9]+$/.test(alias);
}

function isValidCnpj(rawCnpj: string) {
	const cnpj = onlyDigits(rawCnpj);
	if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
		return false;
	}

	const calcDigit = (base: string, weights: number[]) => {
		const total = base
			.split("")
			.reduce((sum, number, index) => sum + Number(number) * weights[index], 0);
		const remainder = total % 11;
		return remainder < 2 ? 0 : 11 - remainder;
	};

	const digit1 = calcDigit(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
	const digit2 = calcDigit(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

	return cnpj.endsWith(`${digit1}${digit2}`);
}

export default function RegisterPage() {
	const { loginWithRedirect, isAuthenticated, logout } = useAuth0();

	const [form, setForm] = useState<FormValues>({
		email: "",
		password: "",
		confirmPassword: "",
		photo: null,
		cardName: "",
		cardNumber: "",
		cardExpiry: "",
		cardCvv: "",
		companyName: "",
		tradeName: "",
		cnpj: "",
		alias: "",
		companyPhone: "",
	});
	const [errors, setErrors] = useState<ValidationErrors>({});
	const [submitMessage, setSubmitMessage] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const photoPreview = useMemo(() => {
		if (!form.photo) {
			return "";
		}
		return URL.createObjectURL(form.photo);
	}, [form.photo]);

	const validateForm = () => {
		const nextErrors: ValidationErrors = {};

		if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
			nextErrors.email = "Informe um e-mail valido.";
		}

		if (!form.password || form.password.length < 8) {
			nextErrors.password = "A senha precisa ter pelo menos 8 caracteres.";
		}

		if (form.confirmPassword !== form.password) {
			nextErrors.confirmPassword = "As senhas precisam ser iguais.";
		}

		if (!form.cardName.trim()) {
			nextErrors.cardName = "Informe o nome impresso no cartao.";
		}

		if (onlyDigits(form.cardNumber).length !== 16) {
			nextErrors.cardNumber = "O cartao deve ter 16 digitos.";
		}

		if (!/^\d{2}\/\d{2}$/.test(form.cardExpiry)) {
			nextErrors.cardExpiry = "Validade no formato MM/AA.";
		}

		if (!/^\d{3,4}$/.test(form.cardCvv)) {
			nextErrors.cardCvv = "CVV deve ter 3 ou 4 digitos.";
		}

		if (!form.companyName.trim()) {
			nextErrors.companyName = "Informe a razao social.";
		}

		if (!form.tradeName.trim()) {
			nextErrors.tradeName = "Informe o nome fantasia.";
		}

		if (!isValidCnpj(form.cnpj)) {
			nextErrors.cnpj = "Informe um CNPJ valido.";
		}

		if (!form.alias || !isValidAlias(form.alias)) {
			nextErrors.alias = "O apelido deve conter apenas letras e numeros, sem espacos ou simbolos.";
		}

		if (onlyDigits(form.companyPhone).length < 10) {
			nextErrors.companyPhone = "Informe um telefone comercial valido.";
		}

		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setSubmitMessage("");

		const isValid = validateForm();
		if (!isValid) {
			setSubmitMessage("Revise os campos destacados para continuar.");
			return;
		}

		const registerPayload = {
			email: form.email,
			company: {
				name: form.companyName,
				tradeName: form.tradeName,
				cnpj: onlyDigits(form.cnpj),
				phone: form.companyPhone,
				enterprise: [form.alias],
			},
			payment: {
				cardName: form.cardName,
				cardNumber: form.cardNumber,
				cardExpiry: form.cardExpiry,
				cardCvv: form.cardCvv,
			},
		};

		localStorage.setItem("pendingRegisterPayload", JSON.stringify(registerPayload));

		try {
			setIsSubmitting(true);
			setSubmitMessage("Finalizando cadastro e redirecionando...");
			await loginWithRedirect({
				authorizationParams: {
					screen_hint: "signup",
					login_hint: form.email,
				},
				appState: {
					returnTo: "/select-app",
					enterprise: [form.alias],
				},
			});
		} catch {
			setSubmitMessage("Nao foi possivel concluir o cadastro agora. Tente novamente.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
			<div className="absolute inset-0 pointer-events-none overflow-hidden">
				<div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl" />
				<div className="absolute bottom-10 -left-24 h-72 w-72 rounded-full bg-violet-100/60 blur-3xl" />
			</div>

			<main className="container-custom relative z-10 py-12 sm:py-16">
				<section className="mx-auto w-full max-w-5xl rounded-3xl border border-slate-200/70 bg-white/90 p-6 sm:p-10 shadow-2xl backdrop-blur-sm">
					<header className="mb-10 text-center">
						<span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
							<UserPlus className="h-4 w-4" />
							Cadastro AVAK
						</span>
						<h1 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900">Criar nova conta</h1>
						<p className="mt-3 text-slate-600 max-w-2xl mx-auto">
							Preencha os dados de acesso, pagamento e empresa para iniciar o onboarding.
						</p>
					</header>

					<form onSubmit={handleSubmit} className="space-y-8">
						<div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 sm:p-6">
							<div className="mb-4 flex items-center gap-2 text-slate-800">
								<ShieldCheck className="h-5 w-5 text-blue-600" />
								<h2 className="text-lg font-bold">Dados de acesso</h2>
							</div>

							<div className="grid gap-4 sm:grid-cols-2">
								<label className="block">
									<span className="text-sm font-semibold text-slate-700">E-mail</span>
									<input
										type="email"
										value={form.email}
										onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
										className={inputClass}
										placeholder="nome@empresa.com"
									/>
									{errors.email ? <p className="mt-1 text-sm text-red-600">{errors.email}</p> : null}
								</label>

                                <label className="block">
									<span className="text-sm font-semibold text-slate-700">Foto de perfil (opcional)</span>
									<div className="mt-1 flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-3">
										<label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700">
											<Upload className="h-4 w-4" />
											Enviar foto
											<input
												type="file"
												accept="image/*"
												className="hidden"
												onChange={e => {
													const file = e.target.files?.[0] ?? null;
													setForm(prev => ({ ...prev, photo: file }));
												}}
											/>
										</label>
										<span className="text-sm text-slate-500 truncate">
											{form.photo ? form.photo.name : "Nenhuma imagem selecionada"}
										</span>
									</div>
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">Senha</span>
									<input
										type="password"
										value={form.password}
										onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
										className={inputClass}
										placeholder="Minimo 8 caracteres"
									/>
									{errors.password ? <p className="mt-1 text-sm text-red-600">{errors.password}</p> : null}
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">Confirmar senha</span>
									<input
										type="password"
										value={form.confirmPassword}
										onChange={e => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
										className={inputClass}
										placeholder="Repita sua senha"
									/>
									{errors.confirmPassword ? <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p> : null}
								</label>

								
							</div>

							{photoPreview ? (
								<div className="mt-4">
									<p className="text-sm font-semibold text-slate-700 mb-2">Preview da foto</p>
									<img
										src={photoPreview}
										alt="Preview da foto"
										className="h-20 w-20 rounded-full border border-slate-200 object-cover"
									/>
								</div>
							) : null}
						</div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 sm:p-6">
							<div className="mb-4 flex items-center gap-2 text-slate-800">
								<Building2 className="h-5 w-5 text-blue-600" />
								<h2 className="text-lg font-bold">Empresa</h2>
							</div>

							<div className="grid gap-4 sm:grid-cols-2">
								<label className="block sm:col-span-2">
									<span className="text-sm font-semibold text-slate-700">Razao social</span>
									<input
										type="text"
										value={form.companyName}
										onChange={e => setForm(prev => ({ ...prev, companyName: e.target.value }))}
										className={inputClass}
										placeholder="Empresa LTDA"
									/>
									{errors.companyName ? <p className="mt-1 text-sm text-red-600">{errors.companyName}</p> : null}
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">Nome fantasia</span>
									<input
										type="text"
										value={form.tradeName}
										onChange={e => setForm(prev => ({ ...prev, tradeName: e.target.value }))}
										className={inputClass}
										placeholder="Restaurante Exemplo"
									/>
									{errors.tradeName ? <p className="mt-1 text-sm text-red-600">{errors.tradeName}</p> : null}
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">Telefone comercial</span>
									<input
										type="tel"
										value={form.companyPhone}
										onChange={e => setForm(prev => ({ ...prev, companyPhone: e.target.value }))}
										className={inputClass}
										placeholder="(11) 99999-9999"
									/>
									{errors.companyPhone ? <p className="mt-1 text-sm text-red-600">{errors.companyPhone}</p> : null}
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">CNPJ</span>
									<input
										type="text"
										value={form.cnpj}
										onChange={e => setForm(prev => ({ ...prev, cnpj: formatCnpj(e.target.value) }))}
										className={inputClass}
										placeholder="00.000.000/0000-00"
									/>
									{errors.cnpj ? <p className="mt-1 text-sm text-red-600">{errors.cnpj}</p> : null}
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">Apelido da empresa</span>
									<input
										type="text"
										value={form.alias}
										onChange={e => setForm(prev => ({ ...prev, alias: e.target.value }))}
										className={inputClass}
										placeholder="somente letras e numeros"
									/>
									{errors.alias ? <p className="mt-1 text-sm text-red-600">{errors.alias}</p> : null}
									<p className="mt-1 text-xs text-slate-500">O apelido sera usado na URL e aceita apenas letras e numeros.</p>
								</label>
							</div>
						</div>
                        
						<div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 sm:p-6">
							<div className="mb-4 flex items-center gap-2 text-slate-800">
								<CreditCard className="h-5 w-5 text-blue-600" />
								<h2 className="text-lg font-bold">Dados de cartao</h2>
							</div>
							<p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
								No momento, os dados de cartao sao apenas de captura de interface. Ainda nao existe implementacao no backend.
							</p>

							<div className="grid gap-4 sm:grid-cols-2">
								<label className="block sm:col-span-2">
									<span className="text-sm font-semibold text-slate-700">Nome no cartao</span>
									<input
										type="text"
										value={form.cardName}
										onChange={e => setForm(prev => ({ ...prev, cardName: e.target.value }))}
										className={inputClass}
										placeholder="Nome como aparece no cartao"
									/>
									{errors.cardName ? <p className="mt-1 text-sm text-red-600">{errors.cardName}</p> : null}
								</label>

								<label className="block sm:col-span-2">
									<span className="text-sm font-semibold text-slate-700">Numero do cartao</span>
									<input
										type="text"
										value={form.cardNumber}
										onChange={e => setForm(prev => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
										className={inputClass}
										placeholder="0000 0000 0000 0000"
									/>
									{errors.cardNumber ? <p className="mt-1 text-sm text-red-600">{errors.cardNumber}</p> : null}
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">Validade</span>
									<input
										type="text"
										value={form.cardExpiry}
										onChange={e => setForm(prev => ({ ...prev, cardExpiry: formatCardExpiry(e.target.value) }))}
										className={inputClass}
										placeholder="MM/AA"
									/>
									{errors.cardExpiry ? <p className="mt-1 text-sm text-red-600">{errors.cardExpiry}</p> : null}
								</label>

								<label className="block">
									<span className="text-sm font-semibold text-slate-700">CVV</span>
									<input
										type="text"
										value={form.cardCvv}
										onChange={e => setForm(prev => ({ ...prev, cardCvv: onlyDigits(e.target.value).slice(0, 4) }))}
										className={inputClass}
										placeholder="123"
									/>
									{errors.cardCvv ? <p className="mt-1 text-sm text-red-600">{errors.cardCvv}</p> : null}
								</label>
							</div>
						</div>
						


						{submitMessage ? (
							<div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
								{Object.keys(errors).length > 0 ? (
									<BadgeAlert className="h-5 w-5 text-amber-600 mt-0.5" />
								) : (
									<CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
								)}
								<p>{submitMessage}</p>
							</div>
						) : null}

						<div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
							{isAuthenticated ? (
								<>
									<Link
										to="/select-app"
										className="btn-primary"
									>
										Ir para o app
									</Link>
									<button
										type="button"
										onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
										className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
									>
										Deslogar
									</button>
								</>
							) : (
								<>
									<Link
										to="/"
										className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
									>
										Voltar para home
									</Link>

									<button type="submit" className="btn-primary" disabled={isSubmitting}>
										{isSubmitting ? "Finalizando..." : "Enviar cadastro"}
									</button>
								</>
							)}
						</div>
					</form>
				</section>
			</main>
		</div>
	);
}
